// CORE-847 — pure geometry for the floating Agapella launcher, split out of the
// widget the same way `widgetVisibility` was: the maths is unit-testable without
// RN, a DOM or a viewport.
//
// The stored point is the AVATAR's top-left corner, not the pill's. In idle the
// widget IS the avatar — the caption only unfolds on hover, into whichever side
// has room. Anchoring on the whole pill would shove the avatar sideways every
// time the caption appeared, which is exactly the kind of jump §3 forbids.

export interface Point { x: number; y: number }
export interface Size { width: number; height: number }
export interface Insets { top: number; right: number; bottom: number; left: number }

export const NO_INSETS: Insets = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * §4 — «отступ от правого и нижнего края не менее 24 px». Held as a minimum on
 * every side, and stacked ON TOP of the device safe-area insets: 24 px measured
 * from the edge of an iPhone viewport still lands under the home indicator, and
 * that overlap is what CORE-731 fixed for the old corner-pinned launcher.
 */
export const WIDGET_SAFE_MARGIN = 24;

/**
 * Native margin — owner's call: «просто 15px от краёв экрана». The 24 px web
 * figure plus a tab-bar clearance parked the icon visibly short of the corner
 * on a phone; 15 px reads as "in the corner" there. Device safe-area insets
 * still stack on top so a notch or home indicator can't swallow the icon.
 *
 * Not in play yet: this module is reached only through `AgapellaWidget.tsx`, and
 * on a phone Metro serves `AgapellaWidget.native.tsx`, whose launcher is pinned
 * to the corner with its own offsets. This — and {@link snapAnchor} below —
 * waits for the native drag pass.
 */
export const NATIVE_EDGE_MARGIN = 15;

/**
 * Owner, 12.10.2026 («сделай как на нативке», after the icon kept moving around
 * on the phone): on a phone-width WEB layout the launcher follows the native
 * rules — it takes its quadrant's corner on drop and it does not relocate
 * itself to dodge page content (CORE-1120's hit-testing stays desktop-web).
 * The same breakpoint the app's screens use for their mobile layout.
 */
export const MOBILE_WEB_MAX_WIDTH = 768;

/** True when a web viewport is narrow enough to be treated like a phone. */
export function isMobileWebLayout(isWeb: boolean, width: number): boolean {
  return isWeb && width < MOBILE_WEB_MAX_WIDTH;
}

/** Idle footprint: the круглый аватар, and nothing else. */
export const AVATAR_SIZE = 60;
/** Gradient ring around the portrait — shared so the ring and the inset the
 *  portrait is drawn at cannot drift apart. */
export const RING_WIDTH = 2.5;

/** §5 — «короткий click/tap не должен ошибочно определяться как drag». */
export const DRAG_THRESHOLD_PX = 8;

/** Pop-up size ceiling; the real size is clamped to the viewport below. */
export const POPUP_MAX: Size = { width: 360, height: 560 };
/** Breathing room between the avatar and the pop-up it opens. */
export const POPUP_GAP = 12;

function clamp(value: number, min: number, max: number): number {
  // A viewport too small to hold the widget plus both margins inverts the
  // range. Prefer the top-left edge there — off-screen to the right is
  // unreachable, off-screen to the left is merely tight.
  if (max < min) return Math.max(0, max);
  return Math.min(Math.max(value, min), max);
}

function bounds(viewport: Size, size: number, insets: Insets, margin: number) {
  return {
    minX: margin + insets.left,
    maxX: viewport.width - size - margin - insets.right,
    minY: margin + insets.top,
    maxY: viewport.height - size - margin - insets.bottom,
  };
}

/**
 * §5 — «виджет нельзя утащить полностью за экран», and the same function
 * normalises a stored point back into the safe area after a resize or a browser
 * zoom (which the viewport reports as a resize).
 */
export function clampAnchor(
  point: Point,
  viewport: Size,
  insets: Insets = NO_INSETS,
  size: number = AVATAR_SIZE,
  margin: number = WIDGET_SAFE_MARGIN,
): Point {
  const b = bounds(viewport, size, insets, margin);
  return { x: clamp(point.x, b.minX, b.maxX), y: clamp(point.y, b.minY, b.maxY) };
}

/** §4 — base position is bottom-right, but never flush against the edge. */
export function defaultAnchor(
  viewport: Size,
  insets: Insets = NO_INSETS,
  size: number = AVATAR_SIZE,
  margin: number = WIDGET_SAFE_MARGIN,
): Point {
  const b = bounds(viewport, size, insets, margin);
  return { x: clamp(b.maxX, b.minX, b.maxX), y: clamp(b.maxY, b.minY, b.maxY) };
}

/**
 * Which side the hover caption unfolds towards. It slides out from UNDER the
 * avatar, so it has to go where the room is — a launcher parked at the right
 * edge (the default) unfolds to the left, one dragged to the left unfolds right.
 */
export function labelSide(anchorX: number, viewport: Size, size: number = AVATAR_SIZE): 'left' | 'right' {
  return anchorX + size / 2 > viewport.width / 2 ? 'left' : 'right';
}

/** The pop-up never exceeds the viewport it has to live in. */
export function popupSize(viewport: Size, insets: Insets = NO_INSETS, margin: number = WIDGET_SAFE_MARGIN): Size {
  const availableW = viewport.width - insets.left - insets.right - margin * 2;
  const availableH = viewport.height - insets.top - insets.bottom - margin * 2;
  return {
    width: Math.max(0, Math.min(POPUP_MAX.width, availableW)),
    height: Math.max(0, Math.min(POPUP_MAX.height, availableH)),
  };
}

/**
 * Where the chat pop-up goes for a given avatar position. Above the avatar when
 * there is room (the familiar bottom-right behaviour), below it when the
 * launcher has been dragged to the top of the screen, and clamped into the safe
 * area in the leftover case — a 560 px pop-up on a short viewport.
 *
 * Horizontally it is centred on the avatar and then clamped, which lands it
 * flush-right at the default position and keeps it on screen everywhere else.
 */
export function popupPlacement(
  anchor: Point,
  viewport: Size,
  insets: Insets = NO_INSETS,
  size: number = AVATAR_SIZE,
  margin: number = WIDGET_SAFE_MARGIN,
): { left: number; top: number; width: number; height: number } {
  const popup = popupSize(viewport, insets, margin);
  const minTop = margin + insets.top;
  const maxTop = viewport.height - popup.height - margin - insets.bottom;

  const above = anchor.y - POPUP_GAP - popup.height;
  const below = anchor.y + size + POPUP_GAP;
  const top =
    above >= minTop ? above
      : below <= maxTop ? below
        : clamp(above, minTop, maxTop);

  const left = clamp(
    anchor.x + size / 2 - popup.width / 2,
    margin + insets.left,
    viewport.width - popup.width - margin - insets.right,
  );

  return { left, top, width: popup.width, height: popup.height };
}

/** True once the pointer has travelled far enough that this is a drag, not a tap. */
export function exceedsDragThreshold(dx: number, dy: number): boolean {
  return Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD_PX;
}

/**
 * Native drop rule — «автоматом занимает угловую часть»: wherever the finger
 * lets go, the icon takes the nearest CORNER, and «влево/вправо, верх/низ
 * считается с середины экрана»: the quadrant the avatar's centre is in at
 * release picks the corner. Free placement stays a web behaviour — on a phone a
 * mid-screen widget sits on top of content with no pointer to nudge it away.
 */
export function snapAnchor(
  point: Point,
  viewport: Size,
  insets: Insets = NO_INSETS,
  size: number = AVATAR_SIZE,
  margin: number = WIDGET_SAFE_MARGIN,
): Point {
  const b = bounds(viewport, size, insets, margin);
  const centerX = point.x + size / 2;
  const centerY = point.y + size / 2;
  return {
    x: centerX <= viewport.width / 2 ? Math.max(0, b.minX) : Math.max(0, b.maxX),
    y: centerY <= viewport.height / 2 ? Math.max(0, b.minY) : Math.max(0, b.maxY),
  };
}

/**
 * Where the launcher may stand while the chat window is open — owner's call
 * (25.08.2026): free placement is fine for the bare icon, but once the window
 * is up the pair has to read as one arrangement rather than as an icon parked
 * mid-page with a panel trailing off it.
 *
 * Six places, all on the safe-area frame: the four corners, and the top and
 * bottom centres. Sides are deliberately absent — the window is 360 px wide and
 * opens above or below the icon, so a launcher docked mid-edge would push it
 * into the same column it already occupies from a corner, with less room.
 *
 * Order is top row then bottom row, left to right, and {@link nearestDock}
 * leans on it only for its (deterministic) tie-break.
 */
export function dockAnchors(
  viewport: Size,
  insets: Insets = NO_INSETS,
  size: number = AVATAR_SIZE,
  margin: number = WIDGET_SAFE_MARGIN,
): Point[] {
  const b = bounds(viewport, size, insets, margin);
  const left = clamp(b.minX, b.minX, b.maxX);
  const right = clamp(b.maxX, b.minX, b.maxX);
  // Centred on the viewport, not on the space between the margins: the icon
  // reads as «по центру» against the page, and a lopsided safe area (a phone
  // held sideways) must not visibly shift it off that centre.
  const middle = clamp((viewport.width - size) / 2, b.minX, b.maxX);
  const top = clamp(b.minY, b.minY, b.maxY);
  const bottom = clamp(b.maxY, b.minY, b.maxY);
  return [
    { x: left, y: top }, { x: middle, y: top }, { x: right, y: top },
    { x: left, y: bottom }, { x: middle, y: bottom }, { x: right, y: bottom },
  ];
}

/**
 * The dock the launcher lands on when it is let go at `point` — nearest by the
 * distance between centres, so the drop reads as «туда, куда я её и вёл».
 *
 * Straight-line distance rather than a quadrant rule on purpose: quadrants send
 * a drop right of centre to the corner even when it was let go a hair from the
 * top-centre dock, which is the kind of "helpful" correction that feels like the
 * widget arguing with the hand.
 */
export function nearestDock(
  point: Point,
  viewport: Size,
  insets: Insets = NO_INSETS,
  size: number = AVATAR_SIZE,
  margin: number = WIDGET_SAFE_MARGIN,
): Point {
  const half = size / 2;
  const cx = point.x + half;
  const cy = point.y + half;
  let best = { x: 0, y: 0 };
  let bestDistance = Infinity;
  for (const dock of dockAnchors(viewport, insets, size, margin)) {
    const dx = dock.x + half - cx;
    const dy = dock.y + half - cy;
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = dock;
    }
  }
  return best;
}

/**
 * Validate whatever came back out of storage. A hand-edited or half-written
 * entry must read as «нет сохранённой позиции» rather than park the launcher at
 * NaN, which renders as an invisible widget with no way back.
 */
export function readStoredAnchor(raw: unknown): Point | null {
  if (!raw || typeof raw !== 'object') return null;
  const { x, y } = raw as Partial<Point>;
  if (typeof x !== 'number' || typeof y !== 'number') return null;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

// ─── CORE-1120 — зоны, занятые интерфейсом страницы ─────────────────────────
//
// До сих пор геометрия знала только про КРАЯ экрана: clampAnchor держит иконку
// внутри viewport минус safe-area минус margin, а CORE-731 добавил к нижнему
// краю полосу на двух маршрутах. Этого хватает, пока перекрывать нечего по
// краям, — и не хватает нигде больше: перетащенная на середину страницы иконка
// остаётся легальной по всем этим правилам и при этом стоит поверх заголовка,
// вкладок или кнопки.
//
// Поэтому у страницы появляется способ сказать «вот прямоугольник, который
// занят»: заголовок, CTA, поле ввода чата, нижняя панель. Иконка обходит их —
// не прячется (продукт этого не разрешает) и не отменяет перетаскивание, а
// смещается в ближайшую точку, где никого не задевает.

/** Прямоугольник в координатах viewport. */
export interface Rect { x: number; y: number; width: number; height: number }

function intersects(a: Rect, b: Rect): boolean {
  // Касание краями — не перекрытие: иконка вплотную к заголовку его не скрывает.
  return a.x < b.x + b.width
    && a.x + a.width > b.x
    && a.y < b.y + b.height
    && a.y + a.height > b.y;
}

/** Занята ли точка: попадает ли квадрат иконки хоть на одну зону. */
export function hitsReservedZone(
  point: Point,
  zones: readonly Rect[],
  size: number = AVATAR_SIZE,
): boolean {
  const box: Rect = { x: point.x, y: point.y, width: size, height: size };
  return zones.some((zone) => intersects(box, zone));
}

/**
 * Ближайшая точка, где иконка никого не перекрывает.
 *
 * Кандидаты — это выходы за каждую задетую зону по четырём сторонам, приведённые
 * обратно в безопасную область. Из тех, что свободны ОТ ВСЕХ зон, берётся
 * ближайший к исходной точке: смещение должно читаться как «подвинулась», а не
 * как «улетела».
 *
 * Если свободного выхода нет вовсе — например, зоны накрыли всю полосу, —
 * пробуем шесть доков, и только потом сдаёмся и возвращаем исходную точку.
 * Сдаёмся именно так, а не пряча иконку: по продукту она обязана оставаться
 * доступной, и перекрытие лучше исчезновения.
 */
export function avoidReservedZones(
  point: Point,
  zones: readonly Rect[],
  viewport: Size,
  insets: Insets = NO_INSETS,
  size: number = AVATAR_SIZE,
  margin: number = WIDGET_SAFE_MARGIN,
): Point {
  if (zones.length === 0) return point;
  if (!hitsReservedZone(point, zones, size)) return point;

  const b = bounds(viewport, size, insets, margin);
  const toSafe = (p: Point): Point => ({
    x: clamp(p.x, b.minX, b.maxX),
    y: clamp(p.y, b.minY, b.maxY),
  });

  const candidates: Point[] = [];
  for (const zone of zones) {
    candidates.push(
      toSafe({ x: point.x, y: zone.y - size }),          // выше зоны
      toSafe({ x: point.x, y: zone.y + zone.height }),   // ниже
      toSafe({ x: zone.x - size, y: point.y }),          // левее
      toSafe({ x: zone.x + zone.width, y: point.y }),    // правее
    );
  }
  candidates.push(...dockAnchors(viewport, insets, size, margin));

  let best: Point | null = null;
  let bestDistance = Infinity;
  for (const candidate of candidates) {
    if (hitsReservedZone(candidate, zones, size)) continue;
    const dx = candidate.x - point.x;
    const dy = candidate.y - point.y;
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }
  return best ?? point;
}

/**
 * Итоговое место иконки — всё правило целиком, одной чистой функцией.
 *
 * Собрано здесь, а не в хуке, ради проверяемости: сценарии из CORE-1120 —
 * восстановление сохранённой точки, переход на другой маршрут, resize, поворот
 * — это разные АРГУМЕНТЫ одного вычисления, и в таком виде их можно проверить
 * без рендера (react-test-renderer в репозитории нет).
 *
 * Порядок обязателен: сначала точка становится законной по краям, потом обходит
 * занятые области. Обратный порядок предлагал бы обход от координаты, лежащей
 * за экраном, и «ближайшее свободное место» считалось бы не от того места.
 *
 * @param stored `null` — человек не перетаскивал; позиция ВЫВОДИТСЯ из окна, а
 *   не помнится. Поэтому сохранённая точка со старого экрана и не может
 *   пережить релиз в виде перекрытия: её каждый раз приводят к текущей
 *   геометрии, а не применяют как есть.
 * @param snap натив в покое — притянуть к углу своей четверти.
 */
export function resolveAnchor(params: {
  stored: Point | null;
  zones?: readonly Rect[];
  viewport: Size;
  insets?: Insets;
  size?: number;
  margin?: number;
  snap?: boolean;
}): Point {
  const {
    stored, zones = [], viewport,
    insets = NO_INSETS, size = AVATAR_SIZE, margin = WIDGET_SAFE_MARGIN, snap = false,
  } = params;
  const placed = placeAnchor({ stored, viewport, insets, size, margin, snap });
  return avoidReservedZones(placed, zones, viewport, insets, size, margin);
}

/**
 * Позиция ДО обхода занятых областей: только края, safe-area и углы натива.
 *
 * Отделена от {@link resolveAnchor} ради проверки живых перекрытий (CORE-1120,
 * QA FAIL на мобильном): проверять надо именно её. Проверка итоговой позиции
 * зациклилась бы — иконка ушла бы с кнопки, перекрытие пропало, зона снялась,
 * иконка вернулась на кнопку. База же от результата не зависит, поэтому ответ
 * устойчив.
 */
export function placeAnchor(params: {
  stored: Point | null;
  viewport: Size;
  insets?: Insets;
  size?: number;
  margin?: number;
  snap?: boolean;
}): Point {
  const {
    stored, viewport,
    insets = NO_INSETS, size = AVATAR_SIZE, margin = WIDGET_SAFE_MARGIN, snap = false,
  } = params;
  if (stored == null) return defaultAnchor(viewport, insets, size, margin);
  return snap
    ? snapAnchor(stored, viewport, insets, size, margin)
    : clampAnchor(stored, viewport, insets, size, margin);
}
